import React from 'react'

import {
	Calendar,
	MoreVertical,
	MapPin,
	Copy,
	Edit,
	Trash2,
} from 'lucide-react'
import { useSelector } from 'react-redux'

import { Schedule } from '@/core/api/entities/schedule'
import useTranslation from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/atoms/Dropdown/Dropdown'
import { LogoIcon } from '@/shared/components/atoms/Logo'
import { cn } from '@/shared/utils/helpers'

import { FavoriteButton } from './FavoriteButton'

interface ScheduleCardProps {
	schedule: Schedule
	onClick?: (schedule: Schedule) => void
	showActions?: boolean // Show three dots menu & favorite button
	onAssign?: (scheduleId: number, scheduleName: string) => void
	onDuplicate?: (
		scheduleId: number,
		scheduleName: string,
		description: string
	) => void
	onEdit?: (scheduleId: number) => void
	onRemove?: (
		scheduleId: number,
		scheduleName: string,
		description: string
	) => void
	removeVariant?: 'dropdown' | 'icon'
	className?: string
}

export const ScheduleCard: React.FC<ScheduleCardProps> = ({
	schedule,
	onClick,
	showActions = false,
	onAssign,
	onDuplicate,
	onEdit,
	onRemove,
	removeVariant = 'dropdown',
	className = '',
}) => {
	const { t, getTranslation } = useTranslation('common')
	const { language } = useSelector((state: RootState) => state.ui)

	const handleCardClick = () => {
		if (onClick) {
			onClick(schedule)
		}
	}

	const covers = schedule.metadata?.mediumCovers?.length
		? schedule.metadata.mediumCovers
		: schedule.metadata?.smallCovers || []
	const thumbnails = covers.slice(0, 2)

	const title = getTranslation(
		schedule.titleTranlations,
		language,
		schedule.title
	)
	const description = getTranslation(
		schedule.descriptionTranlations,
		language,
		schedule.description || ''
	)

	return (
		<div
			className={cn(
				'flex flex-col m-0 h-full bg-transparent md:w-[200px] min-w-[165px] max-w-[200px] transition-transform duration-200 ease-out cursor-pointer hover:-translate-y-[2px]',
				className
			)}
			onClick={handleCardClick}
		>
			<div
				className={cn(
					'relative w-full aspect-square overflow-hidden md:rounded-1xl rounded-lg',
					'bg-[linear-gradient(90deg,var(--card)_0%,var(--surface-light)_42%,var(--shade)_100%)]',
					'dark:bg-[linear-gradient(141.39deg,var(--background)_21.62%,var(--card)_92.09%)]',
					'border border-input dark:border-border shadow-md dark:shadow-none'
				)}
			>
				{/* Header */}
				<div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-2 py-2 md:px-3 md:py-3">
					<div className="flex items-center md:items-start gap-1.5 text-muted-foreground/60 dark:text-muted-foreground/40">
						<Calendar className="md:h-4 md:w-4 h-3 w-3" />
						<span className="block text-sm font-medium text-muted-foreground/60 dark:text-inherit">
							{t('entities.schedule.card.schedule')}
						</span>
					</div>
					{/* Action buttons */}
					<div className="flex items-center gap-1">
						{removeVariant === 'icon' && onRemove && (
							<button
								className="md:h-8 md:w-8 h-6 w-6 rounded-full md:border dark:border-action-item-border md:bg-outline backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors text-white"
								onClick={(e) => {
									e.stopPropagation()
									onRemove(schedule.id, title, description || '')
								}}
							>
								<Trash2 className="h-4 w-4" />
							</button>
						)}
						{showActions && (
							<>
								<span
									id={`schedule-card-dropdown`}
									data-upid={`schedule-card-dropdown`}
								>
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<button
												className="md:h-8 md:w-8 h-6 w-6 rounded-full border border-muted-foreground/20 dark:border-action-item-border bg-outline backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors"
												onClick={(e) => e.stopPropagation()}
											>
												<MoreVertical className="md:h-4 md:w-4 h-3 w-3 dark:text-white text-foreground" />
											</button>
										</DropdownMenuTrigger>
										<DropdownMenuContent
											align="start"
											side="right"
											alignOffset={0}
											sideOffset={10}
											className="w-56"
										>
											{onAssign && (
												<DropdownMenuItem
													onClick={(e) => {
														e.stopPropagation()
														onAssign(schedule.id, title)
													}}
												>
													<MapPin className="mr-2 h-4 w-4" />
													<span>{t('entities.music.actions.assign')}</span>
												</DropdownMenuItem>
											)}
											{onDuplicate && !schedule.public && (
												<DropdownMenuItem
													onClick={(e) => {
														e.stopPropagation()
														onDuplicate(schedule.id, title, description || '')
													}}
												>
													<Copy className="mr-2 h-4 w-4" />
													<span>{t('entities.schedule.card.duplicate')}</span>
												</DropdownMenuItem>
											)}
											{onEdit && !schedule.public && (
												<DropdownMenuItem
													onClick={(e) => {
														e.stopPropagation()
														onEdit(schedule.id)
													}}
												>
													<Edit className="mr-2 h-4 w-4" />
													<span>{t('entities.schedule.card.edit')}</span>
												</DropdownMenuItem>
											)}
											{onRemove && !schedule.public && (
												<DropdownMenuItem
													onClick={(e) => {
														e.stopPropagation()
														onRemove(schedule.id, title, description || '')
													}}
													className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
												>
													<Trash2 className="mr-2 h-4 w-4" />
													<span>{t('entities.schedule.card.remove')}</span>
												</DropdownMenuItem>
											)}
										</DropdownMenuContent>
									</DropdownMenu>
								</span>
								<FavoriteButton
									scheduleId={String(schedule.id)}
									isFavorite={schedule.favorite}
								/>
							</>
						)}
					</div>
				</div>

				{/* Thumbnails */}
				<div className="absolute inset-x-0 bottom-0 md:top-16 top-8 flex md:gap-3 gap-2 pr-5 pt-5 pl-3 pb-3 md:pr-3 md:pt-0 md:pl-3 md:pb-3">
					{/* First thumbnail - Perfect Square */}
					<div className="h-full aspect-square rounded-sm bg-gradient-to-br from-zinc-300 to-zinc-400 dark:from-zinc-700 dark:to-zinc-800 flex-shrink-0 flex items-center justify-center overflow-hidden">
						{thumbnails[0] ? (
							<img
								src={thumbnails[0]}
								alt={title}
								className="h-full w-full object-cover"
							/>
						) : (
							<div className="md:w-12 md:h-12 w-6 h-6">
								<LogoIcon width="100%" height="100%" />
							</div>
						)}
					</div>
					{/* Second thumbnail - Perfect Square, Partially cut off */}
					<div className="h-full aspect-square rounded-sm bg-gradient-to-br from-zinc-300 to-zinc-400 dark:from-zinc-700 dark:to-zinc-800 flex-shrink-0 flex items-center justify-center overflow-hidden">
						{thumbnails[1] ? (
							<img
								src={thumbnails[1]}
								alt={title}
								className="h-full w-full object-cover"
							/>
						) : (
							<div className="md:w-10 md:h-10 w-5 h-5">
								<LogoIcon width="100%" height="100%" />
							</div>
						)}
					</div>
				</div>
			</div>
			{/* Title outside card - reserve enough vertical space for 2 lines */}
			<div className="pt-2 md:pt-4 pb-0 flex items-start min-h-[3.4rem] md:min-h-[4.2rem] px-1 min-w-0 shrink-0">
				<span className="w-full md:text-base text-xs md:font-medium text-foreground line-clamp-2 leading-[1.4] overflow-hidden [overflow-wrap:anywhere] min-w-0">
					{title}
				</span>
			</div>
		</div>
	)
}
